/**
 * Admin "Navigator Tuning" tab (feature 130). Sections live under
 * /admin/navigator-tuning/<section>; add a section by appending to SECTIONS
 * (plan history, plan detail + live tuning, review changes). `render`
 * gets the segments after the section (plan-history/<sessionId> opens the plan detail).
 */
import type { ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ConfigVersionsScreen } from './ConfigVersionsScreen';
import { PlanDetailScreen } from './PlanDetailScreen';
import { PlanHistoryScreen } from './PlanHistoryScreen';
import { ReviewChangesScreen } from './ReviewChangesScreen';

const NAVIGATOR_TUNING_PATH = '/admin/navigator-tuning';

type Section = { value: string; label: string; render: (rest: string[]) => ReactNode };

const SECTIONS: Section[] = [
  {
    value: 'plan-history',
    label: 'Plan history',
    render: ([sessionId]) =>
      sessionId ? <PlanDetailScreen key={sessionId} sessionId={sessionId} /> : <PlanHistoryScreen />,
  },
  { value: 'review-changes', label: 'Review changes', render: () => <ReviewChangesScreen /> },
  { value: 'config-versions', label: 'Config versions', render: () => <ConfigVersionsScreen /> },
];

export function NavigatorTuningTab() {
  // AdminPortal is mounted at /admin/*: the splat is "navigator-tuning/<section>/...".
  const [, sectionParam, ...rest] = useParams()['*']?.split('/') ?? [];
  const active = SECTIONS.find((s) => s.value === sectionParam) ?? SECTIONS[0];

  return (
    <div data-testid="navigator-tuning-tab" className="space-y-6">
      <nav aria-label="Navigator Tuning sections" className="flex flex-wrap gap-2">
        {SECTIONS.map((s) => (
          <Link
            key={s.value}
            to={`${NAVIGATOR_TUNING_PATH}/${s.value}`}
            aria-current={s.value === active.value ? 'page' : undefined}
            className={`rounded-full px-4 py-1.5 text-sm font-semibold ${
              s.value === active.value ? 'bg-bquest-green text-white' : 'bg-white text-[#56607a] hover:text-bquest-green'
            }`}
          >
            {s.label}
          </Link>
        ))}
      </nav>
      {active.render(active.value === sectionParam ? rest : [])}
    </div>
  );
}
